import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import config from '../config/config';
import './CiSetup.css';

const ECOSYSTEMS = [
  { id: 'npm',    label: 'Node.js / npm',  icon: '🟢', desc: 'package.json projects' },
  { id: 'python', label: 'Python / pip',   icon: '🐍', desc: 'requirements.txt / pyproject.toml' },
  { id: 'go',     label: 'Go modules',     icon: '🐹', desc: 'go.mod projects' },
  { id: 'maven',  label: 'Java / Maven',   icon: '☕', desc: 'pom.xml projects' },
  { id: 'gradle', label: 'Java / Gradle',  icon: '🐘', desc: 'build.gradle projects' },
  { id: 'dotnet', label: '.NET / NuGet',   icon: '🔷', desc: '.csproj / .sln projects' },
];

function CiSetup() {
  const [ecosystem, setEcosystem]   = useState('npm');
  const [repoName, setRepoName]     = useState('my-repo');
  const [policy, setPolicy]         = useState('default');
  const [baseUrl, setBaseUrl]       = useState(config.BASE_URL || 'https://your-depguard.com');
  const [policies, setPolicies]     = useState([]);
  const [yaml, setYaml]             = useState('');
  const [copied, setCopied]         = useState(false);
  const [step, setStep]             = useState(1);
  const yamlRef                     = useRef(null);
  const [policyError, setPolicyError] = useState('');
  const [yamlError, setYamlError]   = useState('');

  // Load saved policies for the selector
  useEffect(() => {
    setPolicyError('');
    axios.get(`${config.BASE_URL}/policies`)
      .then(r => setPolicies(r.data || []))
      .catch((e) => setPolicyError(e.response?.data?.error || e.message || 'Failed to load saved policies.'));
  }, []);

  // Re-generate YAML whenever inputs change
  useEffect(() => {
    if (step < 3) return;
    setYamlError('');
    const params = new URLSearchParams({
      ecosystem,
      base_url: baseUrl,
      repo: repoName,
      policy,
    });
    fetch(`${config.BASE_URL}/ci/template?${params}`)
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) throw new Error(text || 'Failed to load workflow template.');
        return text;
      })
      .then(text => setYaml(text))
      .catch((e) => {
        setYaml('# Error loading template');
        setYamlError(e.message || 'Failed to load workflow template.');
      });
  }, [ecosystem, repoName, policy, baseUrl, step]);

  function copyYaml() {
    navigator.clipboard.writeText(yaml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function downloadYaml() {
    const blob = new Blob([yaml], { type: 'application/yaml' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `depguard-${ecosystem}.yml`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="ci-setup">
      <div className="ci-header">
        <div className="ci-header-icon">⚡</div>
        <div>
          <h1 className="ci-title">CI/CD Integration</h1>
          <p className="ci-subtitle">
            Automatically scan every commit — generate a GitHub Actions workflow in seconds.
          </p>
        </div>
      </div>

      {policyError && <div className="alert alert-warning">{policyError}</div>}

      {/* Step indicators */}
      <div className="ci-steps">
        {['Ecosystem', 'Configure', 'Get Workflow'].map((label, i) => (
          <div key={i} className={`ci-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
            <div className="ci-step-num">{step > i + 1 ? '✓' : i + 1}</div>
            <span className="ci-step-label">{label}</span>
            {i < 2 && <div className="ci-step-line" />}
          </div>
        ))}
      </div>

      {/* ── Step 1: Ecosystem ── */}
      {step === 1 && (
        <div className="ci-card">
          <h2 className="ci-card-title">What language/ecosystem is your project?</h2>
          <div className="ci-ecosystem-grid">
            {ECOSYSTEMS.map(e => (
              <button
                key={e.id}
                className={`ci-eco-btn ${ecosystem === e.id ? 'selected' : ''}`}
                onClick={() => setEcosystem(e.id)}
              >
                <span className="ci-eco-icon">{e.icon}</span>
                <span className="ci-eco-label">{e.label}</span>
                <span className="ci-eco-desc">{e.desc}</span>
              </button>
            ))}
          </div>
          <div className="ci-actions">
            <button className="btn btn-primary" onClick={() => setStep(2)}>
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Configure ── */}
      {step === 2 && (
        <div className="ci-card">
          <h2 className="ci-card-title">Configure your integration</h2>
          <div className="ci-form">
            <div className="ci-field">
              <label className="ci-label">Repository name in DepGuard</label>
              <input
                className="form-control"
                value={repoName}
                onChange={e => setRepoName(e.target.value)}
                placeholder="e.g. my-app"
              />
              <p className="ci-hint">
                This must match the repo name used when uploading SBOMs in DepGuard.
              </p>
            </div>
            <div className="ci-field">
              <label className="ci-label">Policy to enforce</label>
              <select className="form-select" value={policy} onChange={e => setPolicy(e.target.value)}>
                <option value="default">default (built-in)</option>
                {policies.map(p => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
              <p className="ci-hint">
                The CI pipeline will fail (exit 1) if this policy is violated.
              </p>
            </div>
            <div className="ci-field">
              <label className="ci-label">DepGuard base URL</label>
              <input
                className="form-control font-mono"
                value={baseUrl}
                onChange={e => setBaseUrl(e.target.value)}
                placeholder="https://your-depguard.com"
              />
            </div>
          </div>
          <div className="ci-actions">
            <button className="btn btn-outline-secondary me-2" onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)}>Generate Workflow →</button>
          </div>
        </div>
      )}

      {/* ── Step 3: YAML + instructions ── */}
      {step === 3 && (
        <div>
          {yamlError && <div className="alert alert-danger">{yamlError}</div>}
          {/* Token setup callout */}
          <div className="ci-callout">
            <div className="ci-callout-icon">🔑</div>
            <div>
              <strong>Before you start:</strong> Add a GitHub secret named{' '}
              <code>DEPGUARD_TOKEN</code> containing a valid DepGuard JWT token.
              <br />
              <span className="text-secondary small">
                GitHub repo → Settings → Secrets and variables → Actions → New repository secret
              </span>
            </div>
          </div>

          {/* YAML block */}
          <div className="ci-yaml-header">
            <div>
              <strong>.github/workflows/depguard-{ecosystem}.yml</strong>
              <span className="ci-yaml-badge ms-2">
                {ECOSYSTEMS.find(e => e.id === ecosystem)?.icon}{' '}
                {ECOSYSTEMS.find(e => e.id === ecosystem)?.label}
              </span>
            </div>
            <div className="ci-yaml-actions">
              <button className="btn btn-sm btn-outline-secondary me-2" onClick={copyYaml}>
                {copied ? '✅ Copied!' : '📋 Copy'}
              </button>
              <button className="btn btn-sm btn-primary" onClick={downloadYaml}>
                ⬇️ Download
              </button>
            </div>
          </div>
          <pre className="ci-yaml-block" ref={yamlRef}><code>{yaml || 'Loading…'}</code></pre>

          {/* How it works */}
          <div className="ci-card mt-4">
            <h2 className="ci-card-title">How it works</h2>
            <div className="ci-steps-explained">
              {[
                { icon: '📦', title: 'Generate SBOM', desc: `CycloneDX tooling scans your ${ECOSYSTEMS.find(e => e.id === ecosystem)?.label} dependencies and produces a machine-readable bill of materials.` },
                { icon: '⬆️', title: 'Upload to DepGuard', desc: 'The SBOM is sent to your DepGuard instance where it is stored and indexed for analysis.' },
                { icon: '🛡️', title: 'Policy Gate', desc: `DepGuard evaluates the SBOM against the "${policy}" policy. If violations are found, the pipeline fails with exit code 1.` },
                { icon: '📊', title: 'View Results', desc: 'Full vulnerability details, license analysis, quality scores and trends are available in the DepGuard dashboard.' },
              ].map((item, i) => (
                <div key={i} className="ci-explained-item">
                  <div className="ci-explained-icon">{item.icon}</div>
                  <div>
                    <div className="ci-explained-title">{item.title}</div>
                    <div className="ci-explained-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="ci-actions mt-3">
            <button className="btn btn-outline-secondary" onClick={() => setStep(2)}>← Edit configuration</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CiSetup;
